import React, { useEffect, useId, useRef, useState } from "react";
import { Search, X, Loader2, Tag, Layers } from "lucide-react";
import { formatMoney } from "../../../lib/retailFormat";

// Sticky customer Search (Sep 2026).
//
// Placement: this renders as a `shrink-0` row INSIDE `KioskShell`'s own
// flex column, between the header and the category tabs. On desktop the
// shell is a fixed-height column where only `<main>` scrolls, so the bar
// is structurally fixed with no `position: sticky` and therefore cannot
// overlap the header, the category pills, the right-hand cart, or the
// checkout button. On mobile the header + search + tabs group sticks as
// ONE unit.
//
// The suggestion panel is absolutely positioned within this bar and
// capped at `min(46vh, 380px)` with its own internal scroll, so at least
// two product rows stay visible behind it — the customer never loses
// their place — and the menu behind it does not reflow while typing.
//
// Accessibility: a full `combobox` + `listbox` pairing with
// `aria-expanded`, `aria-controls`, `aria-activedescendant`, arrow-key
// navigation, Enter to select, Escape to close, and a polite live region
// announcing the result count. Every row is a 48px touch target.
export default function SearchBar({
  query, onQueryChange, suggestions = [], loading = false,
  open, onOpenChange, onSelectProduct, onSelectGroup, onClear,
  facet, onClearFacet, currency, truncated = false, totalProducts,
}) {
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const wrapRef = useRef(null);
  const hasSuggestions = suggestions.length > 0;

  useEffect(() => { setActiveIndex(-1); }, [query]);

  const close = () => {
    onOpenChange(false);
    setActiveIndex(-1);
  };

  // Close on a tap outside the bar — the SAME `mousedown` pattern the
  // admin picker already uses (`retail/admin/AttributeCombobox.jsx`).
  //
  // The panel hangs BELOW the bar over whatever the shell renders next,
  // and it is not dismissed by Escape alone on a touch screen. Until
  // now the row underneath was the category row; with the Product
  // Family row added above it, the first thing the panel covers is the
  // family tabs. A customer who searches and then reaches for another
  // family (or another category) would hit the open panel instead of
  // the tab — their tap would select a suggestion they did not want.
  // Dismissing on an outside tap is what every combobox in this app
  // already does, and it restores both rows the moment the customer
  // looks away from Search. `mousedown` (not `click`) so the panel is
  // gone before the tab underneath receives its own click.
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const select = (suggestion) => {
    if (!suggestion) return;
    if (suggestion.type === "product") {
      // NEVER an add-to-cart. The customer confirms the item on the
      // card itself — the same rule the future voice phase inherits.
      onSelectProduct && onSelectProduct(suggestion);
    } else {
      onSelectGroup && onSelectGroup(suggestion);
    }
    close();
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (query) onClear && onClear();
      close();
      return;
    }
    if (!hasSuggestions) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      onOpenChange(true);
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      onOpenChange(true);
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        select(suggestions[activeIndex]);
      }
    }
  };

  return (
    <div
      ref={wrapRef}
      className="bg-[#0a1a0e] border-b border-emerald-900/40 px-3 sm:px-5 py-2 shrink-0 relative z-30"
      data-testid="retail-search-bar"
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-300/70 pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={open && hasSuggestions}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={
              activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined
            }
            aria-label="Search the menu"
            placeholder="Search by name, brand or size…"
            data-testid="retail-search-input"
            value={query}
            onChange={(e) => { onQueryChange(e.target.value); onOpenChange(true); }}
            onFocus={() => onOpenChange(true)}
            onKeyDown={onKeyDown}
            className="w-full h-11 rounded-md bg-[#0d2413] border border-emerald-300/25 pl-9 pr-10 text-sm text-emerald-50 placeholder:text-emerald-200/40 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
          {loading && (
            <Loader2
              className="w-4 h-4 absolute right-11 top-1/2 -translate-y-1/2 text-emerald-300/70 animate-spin"
              aria-hidden="true"
            />
          )}
          {(query || facet) && (
            <button
              type="button"
              onClick={() => { onClear && onClear(); close(); inputRef.current?.focus(); }}
              data-testid="retail-search-clear"
              aria-label="Clear search"
              className="absolute right-0 top-0 h-11 w-11 grid place-items-center text-emerald-200/70 hover:text-emerald-50"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Applied Brand / Product Family chip. Dismissible, and labelled
          as a filter rather than a sort — it narrows the menu, it does
          not reorder it. */}
      {facet && (
        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="retail-search-facet">
          <span className="text-[10px] uppercase tracking-wider text-emerald-300/70">
            {facet.type === "brand" ? "Brand" : "Product"}
          </span>
          <button
            type="button"
            onClick={onClearFacet}
            data-testid="retail-search-facet-clear"
            aria-label={`Remove ${facet.value} filter`}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md bg-orange-500 text-white text-sm font-medium"
          >
            {facet.value}
            {typeof facet.count === "number" && (
              <span className="text-orange-100/80">({facet.count})</span>
            )}
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {truncated && !facet && !query && (
        <p className="mt-1.5 text-[11px] text-emerald-200/70" data-testid="retail-search-truncated-note">
          Showing the first {totalProducts ? "part of the" : ""} menu — use Search to find anything else.
        </p>
      )}

      {/* Polite announcement so a screen-reader customer knows the list
          changed without it stealing focus from the input. */}
      <span className="sr-only" role="status" aria-live="polite">
        {open && query
          ? `${suggestions.length} result${suggestions.length === 1 ? "" : "s"}`
          : ""}
      </span>

      {open && hasSuggestions && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Search suggestions"
          data-testid="retail-search-suggestions"
          className="absolute left-3 right-3 sm:left-5 sm:right-5 top-full mt-1 bg-[#fdfbf0] text-stone-900 rounded-md border border-emerald-900/20 shadow-xl overflow-y-auto z-40"
          style={{ maxHeight: "min(46vh, 380px)" }}
        >
          {suggestions.map((s, i) => {
            const isGroup = s.type !== "product";
            return (
              <li
                key={isGroup ? `${s.type}-${s.value_normalized || s.value}` : `p-${s.id}`}
                id={`${listId}-option-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                data-testid={
                  isGroup
                    ? `retail-suggestion-${s.type}-${s.value_normalized || s.value}`
                    : `retail-suggestion-product-${s.id}`
                }
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => select(s)}
                className={
                  "min-h-[48px] px-3 py-2 flex items-center gap-3 cursor-pointer border-b border-emerald-900/10 last:border-b-0 " +
                  (i === activeIndex ? "bg-emerald-900/10" : "")
                }
              >
                {isGroup ? (
                  <>
                    {s.type === "brand"
                      ? <Tag className="w-4 h-4 text-emerald-800 shrink-0" aria-hidden="true" />
                      : <Layers className="w-4 h-4 text-emerald-800 shrink-0" aria-hidden="true" />}
                    <span className="flex-1 min-w-0 text-sm">
                      <span className="font-semibold">{s.value}</span>
                      <span className="text-stone-500">
                        {" "}— all {s.type === "brand" ? "products from this brand" : "sizes and brands"}
                      </span>
                    </span>
                    {typeof s.count === "number" && (
                      <span className="text-xs text-stone-500 tabular-nums">{s.count}</span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium truncate">{s.label}</span>
                      <span className="block text-[11px] text-stone-500 truncate">
                        {[s.brand, s.pack_label, s.category].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    {s.is_vegan && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
                        VEGAN
                      </span>
                    )}
                    {s.price !== null && s.price !== undefined && (
                      <span className="text-sm font-semibold tabular-nums">
                        {formatMoney(s.price, s.currency || currency)}
                      </span>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {open && !hasSuggestions && query && !loading && (
        <div
          className="absolute left-3 right-3 sm:left-5 sm:right-5 top-full mt-1 bg-[#fdfbf0] text-stone-900 rounded-md border border-emerald-900/20 shadow-xl px-4 py-4 z-40"
          data-testid="retail-search-empty"
        >
          <div className="text-sm font-medium">No matches for “{query}”.</div>
          <p className="text-xs text-stone-500 mt-1">
            Try a brand, a size, or browse the menu below.
          </p>
        </div>
      )}
    </div>
  );
}
